import { prettyMilliseconds } from "@-/com.npmjs.pretty-ms";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useQueries, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import {
  MorphemeSearchSheet,
  type MorphemeSearchSheetHandle,
} from "@-/dev.improve.haniverse.components.morpheme-search-sheet";
import { getQuizManifestEntryByKey } from "@-/dev.improve.haniverse.assets.slides";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  useGetUserCardAppCardQueryKey,
  userCardAppCardQueryFn,
} from "@-/dev.improve.haniverse.lib.cards.app-card";
import {
  type CardContentKind,
  cardContentDrillType,
  cardContentSubject,
  drillTypeCardContentKind,
  resolveAppCardContent,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  type DueUserCard,
  movePinnedReviewQueueEntry,
  type PinnedReviewQueueEntry,
  removePinnedReviewQueueEntry,
  slotCardIntoPinnedReviewQueue,
  type StudyCardData,
  useReviewQueueQueryOptions,
  useSubjectStudyCardDataListQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { setDeviceSetting, useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { Fragment, type ReactNode, useRef, useState } from "react";
import { AdminPanelButton, AdminPanelSection, AdminPanelStatus } from "#admin-panel-section";

/** How much of the computed queue the editor lists beneath the pinned head. */
const UPCOMING_PREVIEW_COUNT = 8;

const CARD_CONTENT_KIND_LABELS = {
  "grapheme-meaning": "Meaning",
  "grapheme-pronunciation": "Pronunciation",
  "word-characters-in-context": "Word in context",
  "word-characters-in-sentence": "Sentence characters",
  "word-pronunciation-in-sentence": "Sentence speech",
  quiz: "Quiz",
} as const satisfies Record<CardContentKind, string>;

type CardLabel = { detail: string; subject: string };

function entryKey(entry: PinnedReviewQueueEntry): string {
  return `${entry.userCardId}@${entry.pinnedAt}`;
}

function describeDrillType(drillType: DrillType): string {
  const kind = CARD_CONTENT_KIND_LABELS[drillTypeCardContentKind(drillType)];
  return drillType.kind === "meaning" ? kind : `${kind} · ${toDiacriticPinyin(drillType.pinyin)}`;
}

/** A quiz card's prompt, by its catalogue key (`?` for a key this build does not have). */
function quizLabel(key: string): string {
  return getQuizManifestEntryByKey(key)?.title ?? "?";
}

function describeDue(dueMs: number, nowMs: number): string {
  const deltaMs = dueMs - nowMs;
  const distanceMs = Math.abs(deltaMs);
  const distance = prettyMilliseconds(Math.max(60_000, distanceMs), { compact: true });
  return deltaMs <= 0 ? `due ${distance} ago` : `due in ${distance}`;
}

/** What each card is, read from its content (the due scan alone knows no reading). */
function useReviewQueueCardLabels(
  cards: readonly (DueUserCard | null)[],
): (card: DueUserCard | null) => CardLabel {
  const getUserCardAppCardQueryKey = useGetUserCardAppCardQueryKey();
  const userCardIds = uniq(cards.flatMap((card) => (card === null ? [] : [card.userCardId])));
  const labelById = useQueries({
    combine: (results) =>
      new Map(
        userCardIds.flatMap((userCardId, index): [string, CardLabel][] => {
          const appCard = results[index]?.data;
          const content = appCard == null ? null : resolveAppCardContent(appCard);
          return content === null
            ? []
            : [
                [
                  userCardId,
                  content.kind === "quiz"
                    ? { detail: CARD_CONTENT_KIND_LABELS.quiz, subject: quizLabel(content.key) }
                    : {
                        detail: describeDrillType(cardContentDrillType(content)),
                        subject: cardContentSubject(content).text,
                      },
                ],
              ];
        }),
      ),
    queries: userCardIds.map((userCardId) => ({
      queryFn: userCardAppCardQueryFn,
      queryKey: getUserCardAppCardQueryKey(userCardId),
      throwOnError: false,
    })),
  });
  return (card) => {
    if (card === null) {
      return { detail: "Not in this deck", subject: "?" };
    }
    return (
      labelById.get(card.userCardId) ?? {
        detail:
          card.cardContentKind === null ? "…" : CARD_CONTENT_KIND_LABELS[card.cardContentKind],
        subject: card.subject?.text ?? "?",
      }
    );
  };
}

/**
 * The hardcoded review queue: pinned cards the Learn deck serves first, in order and due or not,
 * above a preview of the computed queue that follows them. Every gap between two cards is a slot:
 * its + opens the card search (`MorphemeSearchSheet`) to slot a card in there.
 */
export function AdminPanelReviewQueueSection() {
  const [entries] = useDeviceSetting("pinnedReviewQueue");
  const { data: queue, error } = useQuery({
    ...useReviewQueueQueryOptions(),
    throwOnError: false,
  });
  const searchSheetRef = useRef<MorphemeSearchSheetHandle>(null);
  const [slotIndex, setSlotIndex] = useState(0);
  const [status, setStatus] = useState<string | null>(null);

  // The setting orders the pinned cards (an edit lands in it at once); the query, a moment behind,
  // says which entries are spent and what each card is.
  const pinnedByKey = new Map(
    (queue?.pinnedReviewQueue ?? []).map((pinned) => [entryKey(pinned.entry), pinned]),
  );
  const spentEntries = entries.filter(
    (entry) => pinnedByKey.get(entryKey(entry))?.isReviewed === true,
  );
  const pinnedEntries = entries.filter(
    (entry) => pinnedByKey.get(entryKey(entry))?.isReviewed !== true,
  );
  const pinnedUserCardIds = new Set(pinnedEntries.map((entry) => entry.userCardId));
  const servableUserCardIds = new Set(queue?.pinnedCards.map((card) => card.userCardId));
  const remainingDueCards = (queue?.orderedDueCards ?? []).filter(
    (card) => !pinnedUserCardIds.has(card.userCardId),
  );
  const upcomingCards = remainingDueCards.slice(0, UPCOMING_PREVIEW_COUNT);
  const pinnedCardOf = (entry: PinnedReviewQueueEntry) =>
    pinnedByKey.get(entryKey(entry))?.card ?? null;
  const labelOf = useReviewQueueCardLabels([
    ...entries.map((entry) => pinnedCardOf(entry)),
    ...upcomingCards,
  ]);
  const nowMs = Date.now();

  const write = (next: PinnedReviewQueueEntry[], message: string) => {
    // Spent entries are dropped by any edit: they no longer affect the queue.
    setDeviceSetting("pinnedReviewQueue", next);
    setStatus(message);
  };
  const openSlot = (index: number) => {
    setSlotIndex(index);
    searchSheetRef.current?.open();
  };
  const slotCardIn = (card: StudyCardData) => {
    write(
      slotCardIntoPinnedReviewQueue({
        now: new Date(),
        pinnedEntries,
        slotIndex,
        upcomingUserCardIds: upcomingCards.map((upcoming) => upcoming.userCardId),
        userCardId: card.studyCard.id,
      }),
      `Slotted ${card.grapheme} · ${describeDrillType(card.drillType)} in at #${Math.min(slotIndex, pinnedEntries.length + upcomingCards.length) + 1}`,
    );
    searchSheetRef.current?.close();
  };

  return (
    <AdminPanelSection
      description="Pinned cards come first, in this order, due or not; the computed queue follows. Tap a + to slot a card in there. Rating a pinned card spends it (Undo brings it back)."
      title="Review queue"
    >
      {queue === undefined ? (
        error == null ? (
          <View accessibilityLabel="Reading the review queue" className="h-16 justify-center">
            <ActivityIndicator />
          </View>
        ) : (
          <AdminPanelStatus isError text={error.message} />
        )
      ) : (
        <View testID="admin-panel-review-queue">
          <ReviewQueueSlot index={0} onPress={openSlot} />
          {pinnedEntries.map((entry, index) => {
            const card = pinnedCardOf(entry);
            const label = labelOf(card);
            return (
              <Fragment key={entryKey(entry)}>
                <ReviewQueueRow
                  label={label}
                  note={
                    card === null
                      ? undefined
                      : servableUserCardIds.has(card.userCardId)
                        ? "Pinned"
                        : "Pinned · not servable under the current settings"
                  }
                  position={index + 1}
                  tone={
                    card !== null && servableUserCardIds.has(card.userCardId) ? "pinned" : "warning"
                  }
                >
                  <AdminPanelButton
                    accessibilityLabel={`Move ${label.subject} up`}
                    disabled={index === 0}
                    onPress={() => {
                      write(
                        movePinnedReviewQueueEntry(pinnedEntries, index, -1),
                        `Moved ${label.subject} up`,
                      );
                    }}
                    title="↑"
                  />
                  <AdminPanelButton
                    accessibilityLabel={`Move ${label.subject} down`}
                    disabled={index === pinnedEntries.length - 1}
                    onPress={() => {
                      write(
                        movePinnedReviewQueueEntry(pinnedEntries, index, 1),
                        `Moved ${label.subject} down`,
                      );
                    }}
                    title="↓"
                  />
                  <AdminPanelButton
                    accessibilityLabel={`Unpin ${label.subject}`}
                    onPress={() => {
                      write(
                        removePinnedReviewQueueEntry(pinnedEntries, index),
                        `Unpinned ${label.subject} · ${label.detail}`,
                      );
                    }}
                    title="✕"
                  />
                </ReviewQueueRow>
                <ReviewQueueSlot index={index + 1} onPress={openSlot} />
              </Fragment>
            );
          })}
          {upcomingCards.map((card, index) => (
            <Fragment key={card.userCardId}>
              <ReviewQueueRow
                label={labelOf(card)}
                note={describeDue(card.dueMs, nowMs)}
                position={pinnedEntries.length + index + 1}
                tone="upcoming"
              />
              <ReviewQueueSlot index={pinnedEntries.length + index + 1} onPress={openSlot} />
            </Fragment>
          ))}
          {pinnedEntries.length + upcomingCards.length === 0 ? (
            <Text className="py-1 text-center text-xs text-muted-foreground">
              Nothing is due. Slot a card in to pin it.
            </Text>
          ) : null}
          {remainingDueCards.length > upcomingCards.length ? (
            <Text className="pt-1 text-center text-xs text-muted-foreground">
              {`+${remainingDueCards.length - upcomingCards.length} more due`}
            </Text>
          ) : null}
        </View>
      )}
      {spentEntries.length === 0 ? null : (
        <View className="flex-row items-center gap-3">
          <Text className="flex-1 text-xs text-muted-foreground" numberOfLines={2}>
            {`Reviewed since pinned: ${spentEntries
              .map((entry) => labelOf(pinnedCardOf(entry)).subject)
              .join(" ")}`}
          </Text>
          <AdminPanelButton
            onPress={() => {
              write(pinnedEntries, `Cleared ${spentEntries.length} reviewed`);
            }}
            title="Clear"
          />
        </View>
      )}
      <View className="flex-row items-center gap-3">
        <AdminPanelButton
          disabled={entries.length === 0}
          onPress={() => {
            write([], "Unpinned every card");
          }}
          title="Unpin all"
        />
        {status === null ? null : (
          <View className="flex-1">
            <AdminPanelStatus text={status} />
          </View>
        )}
      </View>
      <MorphemeSearchSheet
        description="Pick a character or word, then which of its cards to slot in."
        ref={searchSheetRef}
        renderSelection={(morpheme) => (
          <ReviewQueueCardChoice
            morpheme={morpheme}
            onChoose={slotCardIn}
            pinnedUserCardIds={pinnedUserCardIds}
          />
        )}
        testID="admin-panel-review-queue-search"
        title={`Slot a card in at #${Math.min(slotIndex, pinnedEntries.length + upcomingCards.length) + 1}`}
      />
    </AdminPanelSection>
  );
}

/** The + between two cards: slots a card in at that position. */
function ReviewQueueSlot({ index, onPress }: { index: number; onPress: (index: number) => void }) {
  return (
    <Pressable
      accessibilityLabel={`Slot a card in at position ${index + 1}`}
      accessibilityRole="button"
      className="h-7 flex-row items-center gap-2 active:opacity-60"
      hitSlop={{ bottom: 2, top: 2 }}
      onPress={() => onPress(index)}
      testID={`admin-panel-review-queue-slot-${index}`}
    >
      <View className="h-px flex-1 bg-border" />
      <View className="size-5 items-center justify-center rounded-full bg-muted">
        <Icon className="text-foreground" name="MdAdd" size={14} />
      </View>
      <View className="h-px flex-1 bg-border" />
    </Pressable>
  );
}

function ReviewQueueRow({
  children,
  label,
  note,
  position,
  tone,
}: {
  children?: ReactNode;
  label: CardLabel;
  note?: string;
  position: number;
  tone: "pinned" | "upcoming" | "warning";
}) {
  return (
    <View
      className={cn("flex-row items-center gap-2.5", tone === "upcoming" && "opacity-60")}
      testID={`admin-panel-review-queue-row-${position}`}
    >
      <Text className="w-5 text-right text-xs text-muted-foreground tabular-nums">{position}</Text>
      <Text className="min-w-8 text-2xl text-foreground">{label.subject}</Text>
      <View className="min-w-0 flex-1">
        <Text className="text-sm text-foreground" numberOfLines={1}>
          {label.detail}
        </Text>
        {note === undefined ? null : (
          <Text
            className={cn(
              "text-xs",
              tone === "warning" ? "text-destructive" : "text-muted-foreground",
            )}
            numberOfLines={1}
          >
            {note}
          </Text>
        )}
      </View>
      {children === undefined ? null : <View className="flex-row gap-1">{children}</View>}
    </View>
  );
}

/** The search sheet's second step: which of the picked morpheme's in-study cards to slot in. */
function ReviewQueueCardChoice({
  morpheme,
  onChoose,
  pinnedUserCardIds,
}: {
  morpheme: MorphemeDetailsTileSlice;
  onChoose: (card: StudyCardData) => void;
  pinnedUserCardIds: ReadonlySet<string>;
}) {
  const { data: cards, error } = useQuery({
    ...useSubjectStudyCardDataListQueryOptions(morpheme.front),
    throwOnError: false,
  });
  const nowMs = Date.now();
  return (
    <View className="gap-3" testID="admin-panel-review-queue-card-choice">
      <View className="flex-row items-center gap-3">
        <Text className="text-4xl text-foreground">{morpheme.front}</Text>
        <Text className="flex-1 text-base text-muted-foreground">Which card?</Text>
      </View>
      {cards === undefined ? (
        error == null ? (
          <ActivityIndicator />
        ) : (
          <AdminPanelStatus isError text={error.message} />
        )
      ) : cards.length === 0 ? (
        <Text className="text-sm text-muted-foreground">
          {`${morpheme.front} has no cards in study yet (or they are switched off in settings). Only studied cards can be reviewed.`}
        </Text>
      ) : (
        cards.map((card) => {
          const isPinned = pinnedUserCardIds.has(card.studyCard.id);
          return (
            <View
              className="flex-row items-center gap-3 rounded-xs bg-surface p-3"
              key={card.studyCard.id}
            >
              <View className="min-w-0 flex-1">
                <Text className="text-base text-foreground">
                  {describeDrillType(card.drillType)}
                </Text>
                <Text className="text-xs text-muted-foreground">
                  {`${describeDue(Date.parse(card.fsrsCard.due), nowMs)}${isPinned ? " · pinned" : ""}`}
                </Text>
              </View>
              <AdminPanelButton
                accessibilityLabel={`${isPinned ? "Move" : "Slot in"} ${card.grapheme} ${describeDrillType(card.drillType)}`}
                onPress={() => onChoose(card)}
                title={isPinned ? "Move here" : "Slot in"}
                variant="primary"
              />
            </View>
          );
        })
      )}
    </View>
  );
}
